import type { ReactNode } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import type { SessionInfo } from '@oms/contracts';
import { useSession } from '../api/auth';
import { ErrorState, PageLoading } from '../components';
import { meets, type Requirement } from '../lib/permissions';
import { SessionContext, useCurrentSession } from './session-context';

function currentPath(loc: { pathname: string; search: string }) {
  return encodeURIComponent(loc.pathname + loc.search);
}

/** Routes for a signed-in tenant user with a selected workspace. */
export function RequireTenantSession() {
  const { data: session, isPending, error, refetch } = useSession();
  const loc = useLocation();
  if (isPending) return <PageLoading />;
  if (error) return <div className="page"><ErrorState error={error} onRetry={() => void refetch()} title="Could not load your session" /></div>;
  if (!session) return <Navigate to={`/login?next=${currentPath(loc)}`} replace />;
  if (session.scope === 'platform') return <Navigate to="/admin" replace />;
  if (!session.tenant) return <Navigate to={`/select-workspace?next=${currentPath(loc)}`} replace />;
  return (
    <SessionContext.Provider value={session}>
      <Outlet />
    </SessionContext.Provider>
  );
}

/** Signed in (tenant scope) but no workspace required: the workspace picker. */
export function RequireSignedIn({ children }: { children: (session: SessionInfo) => ReactNode }) {
  const { data: session, isPending, error, refetch } = useSession();
  const loc = useLocation();
  if (isPending) return <PageLoading />;
  if (error) return <div className="page"><ErrorState error={error} onRetry={() => void refetch()} /></div>;
  if (!session) return <Navigate to={`/login?next=${currentPath(loc)}`} replace />;
  if (session.scope === 'platform') return <Navigate to="/admin" replace />;
  return <>{children(session)}</>;
}

/** Platform administration console routes. */
export function RequirePlatformSession({ wrongScope }: { wrongScope: ReactNode }) {
  const { data: session, isPending, error, refetch } = useSession();
  const loc = useLocation();
  if (isPending) return <PageLoading />;
  if (error) return <div className="page"><ErrorState error={error} onRetry={() => void refetch()} /></div>;
  if (!session) return <Navigate to={`/admin/login?next=${currentPath(loc)}`} replace />;
  if (session.scope !== 'platform') return <>{wrongScope}</>;
  return (
    <SessionContext.Provider value={session}>
      <Outlet />
    </SessionContext.Provider>
  );
}

/** Hides a page from users without the permission the API will check anyway. */
export function RequirePermission({ requires, children }: { requires: Requirement; children: ReactNode }) {
  const session = useCurrentSession();
  if (!meets(session.permissions, requires)) {
    return (
      <div className="page">
        <div className="notice notice-warning" role="alert">
          <strong>You do not have access to this page.</strong>
          <p style={{ margin: '4px 0 0' }}>Ask a workspace administrator if you need it.</p>
        </div>
      </div>
    );
  }
  return <>{children}</>;
}
